import { RequestLineRecord } from '../../../../types/PurchaseRequest';
import React from 'react';
import { Descriptions, Table, Tag, Typography } from 'antd';
import { OrderLineForm } from '../../../../types/PurchaseOrder';

interface PurchaseRequestInfoTableProps {
  requestLines: RequestLineRecord[] | undefined;
}

const { Text } = Typography;

/** The fields the specification sheet asks for, in the order the form asks. */
const SPEC_FIELDS: [keyof RequestLineRecord, string][] = [
  ['specification', 'Composition / thickness / kind'],
  ['size', 'Size'],
  ['length', 'Length'],
  ['width', 'Width'],
  ['height', 'Height'],
  ['diameter', 'Diameter'],
  ['circumference', 'Circumference'],
  ['density', 'Density'],
  ['thickness', 'Thickness'],
  ['otherMeasurements', 'Other measurements'],
  ['testsRequired', 'Tests / certificates required'],
  ['purpose', 'Purpose / use'],
  ['specialConsiderations', 'Special considerations'],
  ['printingInstruction', 'Printing instruction'],
  ['supportMaterial', 'Other supporting material'],
];

const hasSpec = (r: RequestLineRecord) =>
  SPEC_FIELDS.some(([k]) => r[k]) || r.sampleNeeded != null;

const PurchaseRequestInfoTable: React.FC<PurchaseRequestInfoTableProps> = ({
  requestLines,
}) => {
  return (
    <>
      <Table
        rowKey={(record) => record.id}
        dataSource={requestLines}
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        expandable={{
          // Only lines that carry a specification can be opened. A request here
          // runs to hundreds of known materials; an expand arrow on every one
          // of them would suggest there is something behind it when there is not.
          rowExpandable: hasSpec,
          expandedRowRender: (r: RequestLineRecord) => (
            <Descriptions size="small" column={2} bordered>
              {SPEC_FIELDS.filter(([k]) => r[k]).map(([k, label]) => (
                <Descriptions.Item key={String(k)} label={label}>
                  {String(r[k])}
                </Descriptions.Item>
              ))}
              {r.sampleNeeded != null && (
                <Descriptions.Item label="Sample needed" span={2}>
                  {r.sampleNeeded ? (
                    <>
                      <Tag color="blue">Yes</Tag>
                      {r.sampleDateNeeded ? `by ${r.sampleDateNeeded} · ` : ''}
                      {r.sampleQtyMinimum != null
                        ? `minimum ${r.sampleQtyMinimum}`
                        : ''}
                      {r.sampleQtyExcess ? `, plus ${r.sampleQtyExcess} excess` : ''}
                    </>
                  ) : (
                    <Tag>No</Tag>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        }}
      >
        <Table.Column title="Description" dataIndex="description" />
        <Table.Column
          title="Product"
          dataIndex="product"
          render={(_text: any, record: OrderLineForm) => {
            return <span>{record.product?.description}</span>;
          }}
        />
        <Table.Column
          title="Code"
          dataIndex="productCode"
          render={(_text: any, record: OrderLineForm) => {
            return <span>{record.product?.code}</span>;
          }}
        />
        <Table.Column
          title="Product UoM"
          dataIndex="productUoM"
          render={(_text: any, record: OrderLineForm) => {
            return <span>{record.productUom?.description}</span>;
          }}
        />
        <Table.Column title="Request Quantity" dataIndex="quantity" />
        <Table.Column
          title="Specification"
          render={(_t: any, record: RequestLineRecord) =>
            hasSpec(record) ? (
              <Tag color="purple">on file</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )
          }
        />
      </Table>
    </>
  );
};

export default PurchaseRequestInfoTable;
